import { loadConfig } from './config/loadConfig';
import { createPhaserGame } from './game/PhaserGame';
import { mountUI } from './ui/mountUI';
import {
  createEconomy,
  createMultipliers,
  tick,
  managedRates,
  createEventBus,
  now,
} from './core';
import {
  createSaveService,
  createLocalStorageAdapter,
  createPreferencesAdapter,
} from './services/save';
import { createLifecycle } from './services/lifecycle';
import { mountDebugPanel, shouldShowDebugPanel } from './debug/debugPanel';
import type { DebugPanelHandles } from './debug/debugPanel';
import { ensurePreviewProduction } from './debug/previewProduction';
import type { GameState } from './core/state';

const TICK_HZ = 10;
const TICK_DT = 1 / TICK_HZ;

function showUpdateRequired(foundVersion: number | undefined): void {
  const uiRoot = document.getElementById('ui-root');
  const target = uiRoot ?? document.body;
  const el = document.createElement('div');
  el.dataset.testid = 'save-update-required';
  el.setAttribute('role', 'alert');
  el.style.cssText = [
    'position:fixed',
    'inset:0',
    'z-index:100',
    'display:flex',
    'align-items:center',
    'justify-content:center',
    'padding:24px',
    'background:rgba(20,12,8,0.92)',
    'color:#F5E6C8',
    'font:16px/1.4 system-ui,sans-serif',
    'text-align:center',
  ].join(';');
  const ver =
    typeof foundVersion === 'number' ? ` (save v${foundVersion})` : '';
  el.textContent = `Please update the app to continue.${ver} Your save was kept safe.`;
  target.appendChild(el);
}

async function boot(): Promise<void> {
  const config = loadConfig();
  (window as unknown as { __BURROW_CONFIG__?: unknown }).__BURROW_CONFIG__ = config;

  const economy = createEconomy(config.economy);
  const multipliers = createMultipliers(config.economy);
  const events = createEventBus();

  let storage;
  try {
    storage = await createPreferencesAdapter();
  } catch {
    storage = createLocalStorageAdapter();
  }
  const save = createSaveService(config.economy, storage);
  const loaded = await save.load();

  if (loaded.source === 'blocked' || !loaded.state) {
    showUpdateRequired(loaded.foundSchemaVersion);
    return;
  }

  const state: GameState = loaded.state;

  const gameRoot = document.getElementById('game-root');
  const uiRoot = document.getElementById('ui-root');
  if (!gameRoot || !uiRoot) throw new Error('#game-root or #ui-root missing');

  createPhaserGame(gameRoot);
  mountUI();

  // Mutable holder so onOffline (invoked during resume) never hits TDZ.
  let debug: DebugPanelHandles | null = null;

  // Build-time gate only: DEV or VITE_PREVIEW_BUILD. Hostname / ?debug=1 never enable.
  // Inline import.meta.env so production store builds tree-shake the panel + seed
  // (and the `"debug-panel"` string) out of the bundle.
  let showDebug = false;
  if (import.meta.env.DEV || import.meta.env.VITE_PREVIEW_BUILD === '1') {
    showDebug = shouldShowDebugPanel();
    if (showDebug) {
      ensurePreviewProduction(state);
    }
  }

  const sim = { economy, multipliers, events, nowMs: now };
  const lifecycle = createLifecycle(() => state, save, sim, {
    onOffline(result) {
      debug?.setOffline(result);
    },
  }, events);
  await lifecycle.attachNative();

  // Offline check on cold start (may call onOffline; debug still null → no-op)
  const offlineOnBoot = await lifecycle.resume();

  if (
    (import.meta.env.DEV || import.meta.env.VITE_PREVIEW_BUILD === '1') &&
    showDebug
  ) {
    debug = mountDebugPanel(uiRoot, state);
    debug.setOffline(offlineOnBoot);
    debug.setTotalGold(state.currencies.gold);
  }

  const syncHudGold = (gold: number) => {
    const chip = document.querySelector('[data-testid="hud-gold"]');
    if (chip) chip.textContent = String(Math.floor(gold));
  };
  events.on('goldChanged', ({ gold }) => {
    syncHudGold(gold);
    debug?.setTotalGold(gold);
  });
  // Resume ran before this listener existed — sync HUD for offline-applied gold.
  syncHudGold(state.currencies.gold);

  // Autosusave interval from config
  const autosaveMs = (config.economy.save.autosaveSeconds || 15) * 1000;
  window.setInterval(() => {
    void save.save(state);
  }, autosaveMs);

  // Simulation loop at conceptual 10 Hz
  let last = performance.now();
  let acc = 0;
  const loop = (t: number) => {
    const frameDt = Math.min(0.25, (t - last) / 1000);
    last = t;
    acc += frameDt;
    while (acc >= TICK_DT) {
      tick(state, TICK_DT, sim);
      acc -= TICK_DT;
    }
    const rates = managedRates(state, sim);
    const valueMult = multipliers.valueMultiplier({
      siteIndex: state.site.index,
      shrineRichVeins: state.shrine['richVeins'] ?? 0,
      heroHome: !state.hero.expedition || now() >= state.hero.expedition.endsAt,
      goldenLantern: state.entitlements.goldenLantern,
      adBoostActive: now() < state.boosts.adBoostEndsAt,
    });
    debug?.setGoldPerSec(rates.throughput * valueMult);
    debug?.setTotalGold(state.currencies.gold);
    requestAnimationFrame(loop);
  };
  requestAnimationFrame(loop);

  (window as unknown as { __BURROW_STATE__?: GameState }).__BURROW_STATE__ = state;
}

void boot();
